import { withTranslation } from "next-i18next";
import { useMemo } from "react";
import { useRecentlyModifiedFilters } from "@hooks/use-catalog";
import { useProductsRented } from "@hooks/use-current-user";
import Button from "@components/core/Button";
import QuickFilterButton from "./QuickFilterButton";

function QuickFiltersList({
  openFilters,
  setFilterSectionOpened,
  isFilterAvailableChecked,
  categoryItems,
  sizeItems,
  colorItems,
  seasonItems,
  brandItems,
  kindsItems,
  sleeveItems,
  legItems,
  checkedPersonalised,
  listingPage,
  t,
}) {
  const recentlyModifiedFilters = useRecentlyModifiedFilters();
  const productsRented = useProductsRented();

  function openSection(filterSectionKey) {
    setFilterSectionOpened(filterSectionKey);
    openFilters();
  }

  const checkedFilters = useMemo(
    () => ({
      availabilities: isFilterAvailableChecked ? ["available"] : [],
      category: categoryItems.filter((item) => item.isRefined),
      sizes: sizeItems.filter((item) => item.isRefined && item.value !== "TU"),
      colors: colorItems.filter((item) => item.isRefined),
      seasons: seasonItems.filter((item) => item.isRefined),
      brands: brandItems.filter((item) => item.isRefined),
      kinds: kindsItems.filter((item) => item.isRefined),
      length: [
        ...sleeveItems.filter((item) => item.isRefined),
        ...legItems.filter((item) => item.isRefined),
      ],
      personalised: checkedPersonalised === "all" ? [] : [checkedPersonalised],
    }),
    [
      isFilterAvailableChecked,
      categoryItems,
      sizeItems,
      colorItems,
      seasonItems,
      brandItems,
      kindsItems,
      sleeveItems,
      legItems,
      checkedPersonalised,
    ],
  );

  const quickFilterButtonsOrder = useMemo(() => {
    const activeFilters = [];
    const inactiveFilters = [];
    const activeFiltersReordered = [];

    Object.entries(checkedFilters).forEach(([key, value]) => {
      if (value.length) {
        activeFilters.push(key);
      } else {
        inactiveFilters.push(key);
      }
    });

    recentlyModifiedFilters.forEach((recentlyAddedFilter) => {
      if (activeFilters.includes(recentlyAddedFilter)) {
        activeFiltersReordered.push(recentlyAddedFilter);
        activeFilters.splice(activeFilters.indexOf(recentlyAddedFilter), 1);
      }
    });

    activeFiltersReordered.push(...activeFilters);

    const orders = [...activeFiltersReordered, ...inactiveFilters];
    const orderIndexes = orders.reduce((acc, key, idx) => {
      acc[key] = idx + 1;
      return acc;
    }, {});

    return orderIndexes;
  }, [checkedFilters, recentlyModifiedFilters]);

  return (
    <div className="d-flex gap-2">
      <Button
        variant="outline-primary"
        leftIconName="filters"
        size="sm"
        onClick={openFilters}
        trackingName="filterButton"
        title={t("catalog:filters.title")}
        pill
        style={{ order: 0 }}
        className="d-lg-none"
      />

      <Button
        variant="outline-primary"
        leftIconName="filters"
        size="sm"
        onClick={openFilters}
        trackingName="filterButton"
        title={t("catalog:filters.title")}
        pill
        style={{ order: 0 }}
        className="d-none d-lg-block"
      >
        {t("catalog:filters.title")}
      </Button>

      <QuickFilterButton
        filterSectionKey="availabilities"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openFilters}
        hideOnInactive
        hideCount
        alternativeTitle="availabilities_short"
      />

      <QuickFilterButton
        filterSectionKey="category"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        listingPage={listingPage}
        filtersConflictingWithListingPage={["subcategory", "clothes", "accessories"]}
        items={categoryItems}
      />

      <QuickFilterButton
        filterSectionKey="sizes"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        listingPage={listingPage}
        filtersConflictingWithListingPage={["sizes"]}
        items={sizeItems}
      />

      <QuickFilterButton
        filterSectionKey="colors"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        listingPage={listingPage}
        filtersConflictingWithListingPage={["colors"]}
        items={colorItems}
      />

      <QuickFilterButton
        filterSectionKey="seasons"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        listingPage={listingPage}
        filtersConflictingWithListingPage={["seasons"]}
        items={seasonItems}
      />

      <QuickFilterButton
        filterSectionKey="brands"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        listingPage={listingPage}
        filtersConflictingWithListingPage={["brands"]}
        items={brandItems}
      />

      <QuickFilterButton
        filterSectionKey="kinds"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        showSelectedValues
        alternativeTitle="kinds_short"
        listingPage={listingPage}
        filtersConflictingWithListingPage={["kinds"]}
        items={kindsItems}
      />

      <QuickFilterButton
        filterSectionKey="length"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        listingPage={listingPage}
        filtersConflictingWithListingPage={["sleeves", "legs"]}
        items={[...sleeveItems, ...legItems]}
      />

      <QuickFilterButton
        filterSectionKey="personalised"
        checkedFilters={checkedFilters}
        quickFilterButtonsOrder={quickFilterButtonsOrder}
        openSection={openSection}
        showSelectedValues
        listingPage={listingPage}
        filtersConflictingWithListingPage={["personalised"]}
        items={productsRented}
      />
    </div>
  );
}

export default withTranslation("catalog")(QuickFiltersList);
